import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import createMetadata from '@/shared/api/inventory/createMetadata';
import getFormFieldsToSave from '@/shared/components/widgets/metadata/CreateMetadataForm/utils/getFormFieldsToSave';
import useModalDialog from '@/shared/hooks/useModalDialog';
import useNotificationBar from '@/shared/hooks/useNotificationBar';
import useProgressDialog from '@/shared/hooks/useProgressDialog';
import useRedirectLogin from '@/shared/hooks/useRedirectLogin';
import DialogName from '@/shared/types/dialogName.enum';

type OnUpdateDialogState = ReturnType<
  typeof useModalDialog<unknown, DialogName>
>['onUpdateDialogState'];

type OnUpdateNotificationBar = ReturnType<
  typeof useNotificationBar
>['onUpdateNotificationBar'];

type OnUpdateProgressDialog = ReturnType<
  typeof useProgressDialog
>['onUpdateProgressDialog'];

type Args = {
  onUpdateDialogState: OnUpdateDialogState;
  onUpdateNotificationBar: OnUpdateNotificationBar;
  onUpdateProgressDialog: OnUpdateProgressDialog;
};

const useCreateMetadata = ({
  onUpdateDialogState,
  onUpdateNotificationBar,
  onUpdateProgressDialog,
}: Args) => {
  const { t } = useTranslation(['inventory', 'common']);
  const redirectLogin = useRedirectLogin();

  const [refetchMetadata, setRefetchMetadata] = useState(false);

  const onCreateNewMetadata = async (
    formList: ReturnType<typeof getFormFieldsToSave>[],
  ) => {
    onUpdateDialogState(DialogName.CreateMetadata, { loading: true });
    onUpdateProgressDialog({
      open: true,
      progress: 0,
      title: t('inventory:creatingMetadata'),
    });

    const responses = await createMetadata({
      body: formList,
      onUnauthorized: redirectLogin,
    });

    if (responses.every((response) => response.success)) {
      onUpdateProgressDialog({ open: false });
      onUpdateDialogState(DialogName.CreateMetadata, {
        open: false,
        loading: false,
      });
      onUpdateNotificationBar({
        open: true,
        message: t('inventory:metadataCreated'),
        severity: 'success',
      });
      setRefetchMetadata(true);
    } else {
      onUpdateProgressDialog({ open: false });
      onUpdateDialogState(DialogName.CreateMetadata, {
        open: true,
        loading: false,
        errors: responses
          .filter((response) => !response.success)
          .map((response) => response.msg),
      });
    }
  };

  return {
    refetchMetadata,
    onCreateNewMetadata,
    setRefetchMetadata,
  };
};

export default useCreateMetadata;
